var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import BaseCalendar from '@libs/@core/components/date-picker/BaseCalendar';
import { convertToDate, getFormattedDate, isDateValidInRange, isValidDate } from '@libs/@core/components/date-picker/utils/dateUtils';
import { InputDateContent } from '@libs/@core/components/date-picker/styled/date-picker.styled';
import { ArrowCalendarIcon } from '@import/icons';
import BaseIconInput from '@libs/@core/components/date-picker/components/BaseIconInput';
import useCustomTranslation from '@libs/hooks/useCustomTranslation';
/**
 * This is base date picker component that is used to render the date picker view
 *
 * @param value - The selected date value
 *
 * @param multiple - The flag to determine if the calendar is a range date picker
 *
 * @param maxDate - The maximum date that can be selected
 *
 * @param minDate - The minimum date that can be selected
 *
 * @param onChangeDate - The function to handle the date change
 *
 * @returns The base date picker component
 *
 */
var BaseDatePicker = forwardRef(function (_a, ref) {
    var value = _a.value, multiple = _a.multiple, onChangeDate = _a.onChangeDate, maxDate = _a.maxDate, minDate = _a.minDate, format = _a.format, id = _a.id, placeholder = _a.placeholder;
    var t = useCustomTranslation();
    var startFocus = useRef(null);
    var endFocus = useRef(null);
    var pickerRef = useRef(null);
    var _b = useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = useState(null), activeInput = _c[0], setActiveInput = _c[1];
    var _d = useState([]), valueDate = _d[0], setValueDate = _d[1];
    var valueState = useMemo(function () {
        if (!value)
            return ['']; // or ['', ''] if multiple
        if (multiple && Array.isArray(value)) {
            return value.map(function (val) { return getFormattedDate(val) || ''; });
        }
        if (Array.isArray(value)) {
            return [''];
        }
        return [getFormattedDate(value) || ''];
    }, [value, multiple]);
    useImperativeHandle(ref, function () { });
    useEffect(function () {
        var handleClickOutside = function (event) {
            var mouseEvent = event;
            if (isOpen &&
                pickerRef.current &&
                mouseEvent.target instanceof Node &&
                !pickerRef.current.contains(mouseEvent.target)) {
                setIsOpen(false);
                setActiveInput(null);
                setValueDate(valueState);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, valueState]);
    useEffect(function () {
        if (valueState) {
            setValueDate(valueState);
        }
    }, [valueState]);
    var handleInputChange = function (e, type) {
        var date = e.target.value;
        setValueDate(function (prev) {
            var newValue = __spreadArray([], prev, true);
            newValue[type === 'start' ? 0 : 1] = date;
            return newValue;
        });
        if (!isValidDate(date)) {
            return;
        }
        var dateConvert = convertToDate(date);
        if (minDate &&
            maxDate &&
            !isDateValidInRange({
                date: date,
                min: minDate,
                max: maxDate
            })) {
            return;
        }
        if (!multiple) {
            onChangeDate(dateConvert);
            return;
        }
        else {
            onChangeDate([dateConvert]);
        }
        var isOverDate = type === 'end' ? new Date(dateConvert) < (value === null || value === void 0 ? void 0 : value[0]) : new Date(dateConvert) > value[1];
        var isCheckDate = multiple && !isOverDate && !!(valueDate === null || valueDate === void 0 ? void 0 : valueDate[0]) && !!valueDate[1];
        if (isCheckDate) {
            onChangeDate([type === 'start' ? dateConvert : value === null || value === void 0 ? void 0 : value[0], type === 'end' ? dateConvert : value[1]]);
        }
    };
    var handleChangeDateValue = function (rangeDate) {
        setValueDate(rangeDate.map(function (date) { return getFormattedDate(date); }));
    };
    var handleInputFocus = function (type) {
        var _a, _b;
        setIsOpen(true);
        if (Array.isArray(valueDate) && valueDate.length === 0) {
            (_a = startFocus.current) === null || _a === void 0 ? void 0 : _a.focus();
            setActiveInput('start');
        }
        else {
            if (Array.isArray(valueDate) && valueDate.length === 1) {
                (_b = endFocus.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
            setActiveInput(type);
        }
    };
    var handleOpen = function () {
        setIsOpen(true);
    };
    return (_jsxs("div", { id: id, "aria-labelledby": id, className: 'relative w-full bg-secondary-contrastText rounded-[0.375rem] h-full', ref: pickerRef, children: [_jsx("div", { className: 'w-full h-full', children: _jsxs(InputDateContent, { children: [_jsx("input", { ref: startFocus, type: 'text', value: (valueDate === null || valueDate === void 0 ? void 0 : valueDate[0]) || '', onChange: function (e) { return handleInputChange(e, 'start'); }, onFocus: function () { return handleInputFocus('start'); }, className: 'w-full focus:outline-none', placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : t('L000106') }), multiple && (_jsxs(_Fragment, { children: [_jsx("span", { className: 'w-[30px]', children: _jsx(ArrowCalendarIcon, {}) }), _jsx("input", { ref: endFocus, type: 'text', value: valueDate[1] || '', onChange: function (e) { return handleInputChange(e, 'end'); }, onFocus: function () { return handleInputFocus('end'); }, className: 'w-full focus:outline-none', placeholder: t('L000107') })] })), multiple && activeInput && (_jsx("div", { className: 'absolute bottom-0 w-full', children: _jsx("div", { className: "h-[2px] bg-primary-main transform transition-all duration-300 ease-out ".concat(activeInput === 'start'
                                    ? 'translate-x-0 w-[calc(40%)]'
                                    : 'translate-x-[calc(100%+25%)] w-[calc(40%)]') }) })), _jsx(BaseIconInput, { onClick: handleOpen })] }) }), isOpen && (_jsx(BaseCalendar, { value: value, activeInput: activeInput, multiple: multiple, minDate: minDate, maxDate: maxDate, format: format, onChangeDate: onChangeDate, handleChangeDateValue: handleChangeDateValue, handleInputFocus: handleInputFocus }))] }));
});
export default memo(BaseDatePicker);
